import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import axios from 'axios';
import { 
  Search, Filter, Download, RefreshCw, ChevronLeft, ChevronRight,
  CreditCard, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle,
  AlertCircle, DollarSign, TrendingUp, Wallet, Eye, MoreVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

export default function Transactions() {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [crediting, setCrediting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (typeFilter) params.append('type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await axios.get(`${BACKEND_URL}/api/admin/transactions?${params}`, { headers });
      setTransactions(res.data.transactions || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, statusFilter, searchQuery, headers]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTransactions();
  };

  const handleCreditTransaction = async (txnId) => {
    if (!window.confirm('Are you sure you want to manually credit this transaction?')) return;
    
    setCrediting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/transactions/${txnId}/credit`, {}, { headers });
      toast.success('Transaction credited successfully');
      fetchTransactions();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit transaction');
    } finally {
      setCrediting(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'topup': return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
      case 'payout': return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      case 'booking_earnings': return <DollarSign className="w-4 h-4 text-blue-600" />;
      case 'commission': return <TrendingUp className="w-4 h-4 text-purple-600" />;
      default: return <CreditCard className="w-4 h-4 text-slate-600" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      topup: 'Top-up',
      payout: 'Payout',
      booking_earnings: 'Booking Earnings',
      commission: 'Commission',
      booking_payment: 'Booking Payment'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700',
      processing: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-slate-100 text-slate-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-PH', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
            <p className="text-slate-500 text-sm">Monitor all PayMongo transactions and wallet activity</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchTransactions} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowDownLeft className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Top-ups</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(stats.total_topups)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Payouts</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(stats.total_payouts)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Transactions</p>
                <p className="text-lg font-bold text-slate-900">{total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-lg font-bold text-slate-900">
                  {stats.by_type?.topup?.count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by user email, name, or transaction ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </form>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Types</option>
              <option value="topup">Top-ups</option>
              <option value="payout">Payouts</option>
              <option value="booking_earnings">Earnings</option>
              <option value="commission">Commission</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
              <p className="text-slate-500">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No transactions found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Transaction</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-mono text-xs text-slate-600">{tx.id?.slice(0, 8)}...</p>
                            {tx.paymongo_session_id && (
                              <p className="text-xs text-slate-400">PM: {tx.paymongo_session_id?.slice(0, 12)}...</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {tx.user ? (
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{tx.user.name}</p>
                              <p className="text-xs text-slate-500">{tx.user.email}</p>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(tx.type)}
                            <span className="text-sm text-slate-700">{getTypeLabel(tx.type)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${tx.type === 'topup' || tx.type === 'booking_earnings' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.type === 'topup' || tx.type === 'booking_earnings' ? '+' : '-'}
                            {formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(tx.status)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-600">{formatDate(tx.created_at)}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedTransaction(tx); setShowDetailModal(true); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} transactions
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm text-slate-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Transaction Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Transaction Details</DialogTitle>
              <DialogDescription>
                Full details for transaction {selectedTransaction?.id?.slice(0, 8)}...
              </DialogDescription>
            </DialogHeader>
            
            {selectedTransaction && (
              <div className="space-y-4">
                {/* Status Banner */}
                <div className={`p-3 rounded-lg flex items-center gap-3 ${
                  selectedTransaction.status === 'completed' ? 'bg-green-50' :
                  selectedTransaction.status === 'pending' ? 'bg-amber-50' : 'bg-red-50'
                }`}>
                  {selectedTransaction.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : selectedTransaction.status === 'pending' ? (
                    <Clock className="w-5 h-5 text-amber-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      selectedTransaction.status === 'completed' ? 'text-green-700' :
                      selectedTransaction.status === 'pending' ? 'text-amber-700' : 'text-red-700'
                    }`}>
                      {selectedTransaction.status?.charAt(0).toUpperCase() + selectedTransaction.status?.slice(1)}
                    </p>
                    <p className="text-xs text-slate-500">{formatDate(selectedTransaction.created_at)}</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Transaction ID</p>
                    <p className="font-mono text-sm text-slate-900 break-all">{selectedTransaction.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Type</p>
                    <div className="flex items-center gap-2">
                      {getTypeIcon(selectedTransaction.type)}
                      <span className="text-sm">{getTypeLabel(selectedTransaction.type)}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Amount</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedTransaction.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(selectedTransaction.amount))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Fee</p>
                    <p className="text-sm text-slate-700">{formatCurrency(selectedTransaction.fee_amount || 0)}</p>
                  </div>
                </div>

                {/* User Info */}
                {selectedTransaction.user && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">User</p>
                    <p className="font-medium text-slate-900">{selectedTransaction.user.name}</p>
                    <p className="text-sm text-slate-600">{selectedTransaction.user.email}</p>
                    <p className="text-xs text-slate-500 mt-1">Role: {selectedTransaction.user.role}</p>
                  </div>
                )}

                {/* PayMongo Info */}
                {selectedTransaction.paymongo_session_id && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 mb-2">PayMongo Session</p>
                    <p className="font-mono text-xs text-blue-800 break-all">{selectedTransaction.paymongo_session_id}</p>
                  </div>
                )}

                {/* Admin Actions */}
                {selectedTransaction.status === 'pending' && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => handleCreditTransaction(selectedTransaction.id)}
                      disabled={crediting}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {crediting ? 'Crediting...' : 'Manually Credit This Transaction'}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      This will add {formatCurrency(Math.abs(selectedTransaction.amount))} to the user's wallet
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
